import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SceneRenderer from '../components/SceneRenderer';
import FloorPlanEditor from '../components/FloorPlanEditor';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { supabase } from '../utils/supabase';
import { 
  CubeIcon, 
  MapIcon, 
  ViewColumnsIcon,
  Square2StackIcon
} from '@heroicons/react/24/outline';

const DesignPage = () => {
  const [furnitureModels, setFurnitureModels] = useState([]);
  const [selectedFurniture, setSelectedFurniture] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('3d'); // '2d', '3d', 'split'
  const [floorPlan, setFloorPlan] = useState(null);

  // Load available furniture models from catalog
  useEffect(() => {
    const loadCatalogData = async () => {
      try {
        const response = await fetch('/models/catalog.json');
        const catalogData = await response.json();
        setAvailableModels(catalogData.products || []);
      } catch (error) {
        console.error('Katalog yüklenemedi:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCatalogData();
  }, []);

  // Add furniture to scene
  const addFurnitureToScene = async (catalogItem) => {
    try {
      const loader = new GLTFLoader();
      const modelPath = `/models/${catalogItem.model}`;
      
      const gltf = await new Promise((resolve, reject) => {
        loader.load(
          modelPath,
          resolve,
          undefined,
          reject
        );
      });

      const newFurniture = {
        id: Date.now() + Math.random(),
        name: catalogItem.name,
        model: gltf,
        originalData: catalogItem,
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        texture: {
          color: '#8B4513',
          pattern: 'wood'
        }
      };

      setFurnitureModels(prev => [...prev, newFurniture]);
    } catch (error) {
      console.error('Model yüklenemedi:', error);
      alert('Model yüklenirken bir hata oluştu: ' + catalogItem.name);
    }
  };

  // Update furniture position/rotation
  const handleFurnitureUpdate = (furnitureId, updates) => {
    setFurnitureModels(prev =>
      prev.map(furniture =>
        furniture.id === furnitureId
          ? { ...furniture, ...updates }
          : furniture
      )
    );
  };

  // Remove furniture from scene
  const removeFurniture = (furnitureId) => {
    setFurnitureModels(prev => prev.filter(f => f.id !== furnitureId));
    if (selectedFurniture && selectedFurniture.id === furnitureId) {
      setSelectedFurniture(null);
    }
  };


  // Handle floor plan changes
  const handleFloorPlanChange = (planData) => {
    setFloorPlan(planData);
  };

  // Convert 2D plan to 3D room dimensions (simplified)
  const get3DRoomFromPlan = () => {
    if (!floorPlan || !floorPlan.dimensions) {
      return { width: 5, depth: 4, height: 2.7 };
    }
    
    return {
      width: Math.max(3, floorPlan.dimensions.width),
      depth: Math.max(3, floorPlan.dimensions.height),
      height: 2.7
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">3D Tasarım Stüdyosu yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-50"
    >
      <div className="flex h-screen">
        {/* Main Content Area */}
        <div className="flex-1 relative">
          {viewMode === '2d' && (
            /* 2D Floor Plan Editor */
            <FloorPlanEditor 
              onPlanChange={handleFloorPlanChange}
              initialPlan={floorPlan}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          )}
          
          {viewMode === '3d' && (
            /* 3D Scene Renderer */
            <SceneRenderer
              furnitureModels={furnitureModels}
              selectedFurniture={selectedFurniture}
              onFurnitureSelect={setSelectedFurniture}
              onFurnitureUpdate={handleFurnitureUpdate}
              roomDimensions={get3DRoomFromPlan()}
              floorPlan={floorPlan}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />
          )}
          
          {viewMode === 'split' && (
            /* Split View */
            <div className="flex h-full">
              <div className="flex-1 border-r border-gray-200">
                <div className="h-8 bg-gray-100 border-b border-gray-200 flex items-center px-3">
                  <MapIcon className="h-4 w-4 text-gray-600 mr-2" />
                  <span className="text-sm font-medium text-gray-700">2D Floor Plan</span>
                </div>
                <div className="h-[calc(100%-2rem)]">
                  <FloorPlanEditor 
                    onPlanChange={handleFloorPlanChange}
                    initialPlan={floorPlan}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="h-8 bg-gray-100 border-b border-gray-200 flex items-center px-3">
                  <CubeIcon className="h-4 w-4 text-gray-600 mr-2" />
                  <span className="text-sm font-medium text-gray-700">3D View</span>
                </div>
                <div className="h-[calc(100%-2rem)]">
                  <SceneRenderer
                    furnitureModels={furnitureModels}
                    selectedFurniture={selectedFurniture}
                    onFurnitureSelect={setSelectedFurniture}
                    onFurnitureUpdate={handleFurnitureUpdate}
                    roomDimensions={get3DRoomFromPlan()}
                    floorPlan={floorPlan}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

    </motion.div>
  );
};

export default DesignPage; 